import { useSelector } from 'react-redux';
import { sumBy } from 'lodash';
import { isBookingFromSysAdmin, isSimpleBooking } from 'utils/helpers/booking';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { getQTVehicleTypes } from 'redux/selectors/types';

export const useCurrentBookingState = () => {
  const currentBooking = useSelector(getCurrentBooking);
  const vehicleTypes = useSelector(
    (state) => state.sealink?.vehicle_types || []
  );
  const vehicleTypesFromQT = useSelector(getQTVehicleTypes);
  const {
    maximum_vehicle_length: maximumVehicleLength,
    maximum_car_length: maxCarLength,
  } = useSelector((state) => state.sealink?.vehicle_config || {});
  const luggageTypes = useSelector(
    (state) => state.sealink?.luggage?.luggageTypes || []
  );
  const allowedVehicleTypes = vehicleTypes
    .map((vehicleType) => vehicleType.qt_vehicle_type_id)
    .concat(
      Object.values(luggageTypes).map(
        (luggageType) => luggageType.qtLuggageTypeId
      )
    );

  const isNewBooking =
    currentBooking.state === 'new' || currentBooking.state === undefined;

  const isEditing = !isNewBooking;
  const isActive = currentBooking.state === 'active';

  const isBookingPayable =
    currentBooking && currentBooking?.reservation_ids?.length > 0;

  const derivedSimpleBooking = currentBooking
    ? isSimpleBooking(currentBooking, allowedVehicleTypes)
    : undefined;

  const reservations = currentBooking?.reservations_attributes || [];
  const restrictedReservations = reservations.filter(
    (reservation) => reservation.time_restricted
  );
  const totalVehileLength = sumBy(
    currentBooking.vehicles_attributes,
    (vehicle) => Number(vehicle.length)
  );
  const trailerIncluded = currentBooking.vehicles_attributes?.some(
    ({ vehicle_type_id: qtVehicleTypeId }) => {
      const qtVehicleType = vehicleTypesFromQT.find(
        ({ id }) => id === qtVehicleTypeId
      );
      return qtVehicleType?.trailer;
    }
  );
  const maxLength = trailerIncluded ? maximumVehicleLength : maxCarLength;
  const reachedMaxVehicleLength = totalVehileLength > maxLength;
  // If it is from sys admin, we assume it is respax booking, so cannot modify
  const fromSysAdmin = isBookingFromSysAdmin(currentBooking);
  const canEditBooking =
    (isNewBooking ||
      (derivedSimpleBooking &&
        restrictedReservations.length < reservations.length)) &&
    !fromSysAdmin &&
    !reachedMaxVehicleLength;
  const canEditReturnOnly = canEditBooking && restrictedReservations.length;
  const isFullyPaid =
    currentBooking.balance_in_cents <= 0 && currentBooking.state === 'active';

  return {
    isNewBooking,
    isEditing,
    isActive,
    isSimpleBooking: derivedSimpleBooking,
    canEditBooking,
    canEditReturnOnly,
    isBookingPayable,
    reachedMaxVehicleLength,
    maxLength,
    isFullyPaid,
  };
};
